/**
 * HackingInfraPage · Iter-38
 * ---------------------------------------------------------------------------
 * The Hacking Infrastructure hub — 11 curated tiles split across three lanes:
 *   • LIVE lookup tools (free-API backed, quota-gated)
 *   • PAID infrastructure (residential proxies, VPS, VPN, datacenter pool)
 *   • OSINT (username tracer, breach cross-link)
 *
 * Every free-tool result includes cross-sell "next-step" cards pointing to
 * paid ErrorHacker services — turning every scan into a lead-magnet.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Server, Globe, Database, Shield, Terminal, Zap, Fingerprint, Bug,
  Radar, Search, Loader2, ArrowRight, Crown, Sparkles, Copy, ExternalLink,
  ChevronDown, ChevronUp, MessageSquare, X, Send, Lock, Cpu, Wifi, Swords,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// ---------------------------------------------------------------------------
// Tile catalogue — 11 services (7 live + 4 paid)
// ---------------------------------------------------------------------------
const TILES = [
  // -- LIVE, FREE-API BACKED -------------------------------------------------
  { id: 'ip-intel',       kind: 'live', icon: Radar,      accent: '#4de0ff', name: 'IP Threat Intel',      cat: 'DATA · LIVE',
    tag: 'is this IP malicious?',
    bullets: ['AbuseIPDB confidence score', 'geo · ASN · ISP · Tor / proxy flags', '24/7 fresh data'],
    inputHint: 'e.g. 8.8.8.8 or example.com', inputPlaceholder: 'IP or hostname', apiFn: 'infraIpIntel', field: 'target' },
  { id: 'whois',          kind: 'live', icon: Globe,      accent: '#ffd34d', name: 'WHOIS Lookup',         cat: 'DATA · LIVE',
    tag: 'who owns this domain?',
    bullets: ['registrar · registrant · nameservers', 'creation, expiry & update dates', 'unmasks free hosting scams'],
    inputHint: 'e.g. github.com', inputPlaceholder: 'domain', apiFn: 'infraWhois', field: 'domain' },
  { id: 'dns',            kind: 'live', icon: Database,   accent: '#c084fc', name: 'DNS Recon',            cat: 'DATA · LIVE',
    tag: 'full DNS map',
    bullets: ['A · AAAA · MX · NS · TXT · CNAME', 'SPF/DKIM/DMARC hint', 'validates mail deliverability'],
    inputHint: 'e.g. github.com', inputPlaceholder: 'domain', apiFn: 'infraDns', field: 'domain' },
  { id: 'subdomains',     kind: 'live', icon: Fingerprint,accent: '#ff2d92', name: 'Subdomain Recon',      cat: 'OSINT · LIVE',
    tag: 'attack-surface map',
    bullets: ['HackerTarget + crt.sh combined', 'up to 100 subs per scan', 'catches forgotten staging + admin panels'],
    inputHint: 'e.g. example.com', inputPlaceholder: 'root domain', apiFn: 'infraSubdomains', field: 'domain' },
  { id: 'http-fingerprint',kind: 'live', icon: Cpu,       accent: '#00ff9d', name: 'HTTP Fingerprint',     cat: 'OSINT · LIVE',
    tag: 'what runs behind this URL?',
    bullets: ['detects tech stack (React, Next, PHP, Nginx, …)', 'headers · WAF · CDN', 'flags mixed content + missing security headers'],
    inputHint: 'e.g. https://errorhacker.site', inputPlaceholder: 'https://…', apiFn: 'infraHttpFingerprint', field: 'url' },
  { id: 'cve-search',     kind: 'live', icon: Bug,        accent: '#ff8a4d', name: 'CVE Search',           cat: 'DATA · LIVE',
    tag: 'known vulnerabilities',
    bullets: ['NVD-backed · always fresh', 'by CVE-ID or keyword', 'CVSS · vector · patch link'],
    inputHint: 'e.g. CVE-2021-44228 or "apache log4j"', inputPlaceholder: 'CVE-ID or keyword', apiFn: 'infraCveSearch', field: 'query' },
  { id: 'username-osint', kind: 'live', icon: Search,     accent: '#4de0ff', name: 'Username OSINT',       cat: 'OSINT · LIVE',
    tag: 'find every account for this handle',
    bullets: ['28+ platforms · parallel scan', 'GitHub · X · Instagram · TikTok · Reddit · …', 'catches impersonators fast'],
    inputHint: 'e.g. linus, torvalds — no @', inputPlaceholder: 'username', apiFn: 'infraUsernameOsint', field: 'username' },

  // -- PAID INFRASTRUCTURE ---------------------------------------------------
  { id: 'residential-proxies', kind: 'paid', icon: Wifi, accent: '#00ff9d', name: 'Residential Proxies', cat: 'INFRASTRUCTURE',
    tag: 'blend in anywhere on Earth',
    bullets: ['195+ countries · rotating IPs', 'sticky sessions (10–120 min)', 'OSINT-grade — carrier-detectable at 0%', 'unlimited bandwidth pools available'],
    priceHint: 'from ₹1,499 / mo', badge: 'MOST POPULAR' },
  { id: 'datacenter-proxies', kind: 'paid', icon: Server, accent: '#4de0ff', name: 'Datacenter Proxies', cat: 'INFRASTRUCTURE',
    tag: 'raw speed · bulk automation',
    bullets: ['dedicated /24 pools', 'unlimited concurrent threads', 'sub-100ms SEA & EU latency', 'perfect for scraping + testing'],
    priceHint: 'from ₹499 / mo' },
  { id: 'anon-vps',           kind: 'paid', icon: Terminal, accent: '#c084fc', name: 'Anonymous VPS', cat: 'INFRASTRUCTURE',
    tag: 'a private lab in the cloud',
    bullets: ['pay in crypto · zero KYC', 'custom OS · Kali / Parrot / plain Debian', 'DDoS-protected · SSD-backed', 'built for pentesting labs & red-team ops'],
    priceHint: 'from ₹899 / mo' },
  { id: 'private-vpn',        kind: 'paid', icon: Shield, accent: '#ff2d92', name: 'Private VPN Tunnel', cat: 'INFRASTRUCTURE',
    tag: 'your own no-log tunnel',
    bullets: ['dedicated IP · single tenant', 'WireGuard + OpenVPN', 'India · US · EU exits', 'no logs · no monthly rotation'],
    priceHint: 'from ₹299 / mo' },
];

// ---------------------------------------------------------------------------
// Small helper: absolute URL for internal API assets when needed
// ---------------------------------------------------------------------------
const cats = [...new Set(TILES.map(t => t.cat))];

const HackingInfraPage = () => {
  const [openId, setOpenId] = useState(null);
  const [quoteFor, setQuoteFor] = useState(null); // tile object
  const [params] = useSearchParams();

  // Deep-link support: /hacking-servers?quote=residential-proxies opens the quote modal automatically
  useEffect(() => {
    const q = params.get('quote');
    if (q) {
      const t = TILES.find(x => x.id === q);
      if (t) setQuoteFor(t);
    }
  }, [params]);

  return (
    <div className="min-h-screen bg-[var(--eh-bg)] text-white pb-24">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--eh-border)]" data-testid="infra-hero">
        <div className="absolute inset-0 pointer-events-none opacity-40 bg-hero-mesh" aria-hidden />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--eh-border)] text-[10px] eh-mono tracking-widest opacity-80 mb-6">
            <Swords size={11} className="text-[#ffd34d]" /> HACKING · INFRASTRUCTURE
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight nameplate-serif">
            <span className="block">the servers,</span>
            <span className="block eh-neon">the data, the ops kit.</span>
          </h1>
          <p className="mt-5 text-sm sm:text-base opacity-70 max-w-2xl mx-auto leading-relaxed">
            Free real-time recon backed by 8 public intel APIs — anonymous proxies, VPS, VPN and private data-lookups on the paid side.
            Every scan you run here is a starting point; we do the follow-up for you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="#live" className="eh-btn-primary" data-testid="infra-jump-live"><Zap size={14} /> TRY LIVE TOOLS</a>
            <a href="#paid" className="eh-btn-ghost" data-testid="infra-jump-paid"><Server size={14} /> INFRASTRUCTURE</a>
          </div>
        </div>
      </section>

      {/* TILES BY CATEGORY */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-14">
        {cats.map((cat) => {
          const catTiles = TILES.filter(t => t.cat === cat);
          const anchor = catTiles[0]?.kind === 'paid' ? 'paid' : 'live';
          return (
            <section id={anchor} key={cat}>
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="eh-mono text-[11px] tracking-[0.3em] opacity-70">// {cat}</h2>
                <span className="text-[10px] eh-mono opacity-50">{catTiles.length} · TILES</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {catTiles.map(t => (
                  <InfraTile
                    key={t.id}
                    tile={t}
                    isOpen={openId === t.id}
                    onToggle={() => setOpenId(openId === t.id ? null : t.id)}
                    onRequestQuote={() => setQuoteFor(t)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {/* Quote modal */}
      {quoteFor && (
        <QuoteRequestModal
          tile={quoteFor}
          onClose={() => setQuoteFor(null)}
        />
      )}

      {/* Local styling */}
      <style>{`
        .bg-hero-mesh {
          background:
            radial-gradient(ellipse at 15% 0%, rgba(0,255,157,.08), transparent 50%),
            radial-gradient(ellipse at 85% 20%, rgba(77,224,255,.06), transparent 55%),
            radial-gradient(ellipse at 60% 90%, rgba(255,211,77,.05), transparent 60%);
        }
        .nameplate-serif {
          font-family: 'Cinzel', 'Playfair Display', serif;
          letter-spacing: 0.005em;
        }
        .infra-tile {
          position: relative;
          padding: 22px 20px;
          border-radius: 16px;
          background:
            radial-gradient(ellipse at 15% 0%, color-mix(in srgb, var(--c) 14%, transparent), transparent 60%),
            linear-gradient(160deg, rgba(255,255,255,.03), rgba(0,0,0,.42));
          border: 1px solid color-mix(in srgb, var(--c) 30%, transparent);
          overflow: hidden;
          transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s, box-shadow .35s;
        }
        .infra-tile:hover {
          border-color: var(--c);
          transform: translateY(-2px);
          box-shadow: 0 20px 48px -18px color-mix(in srgb, var(--c) 60%, transparent),
                      0 0 0 1px rgba(255,211,77,.25) inset;
        }
        .infra-tile::before {
          content: ''; position: absolute; inset: 0; border-radius: inherit;
          padding: 1px; pointer-events: none;
          background: linear-gradient(160deg, rgba(255,211,77,.35), transparent 40%, color-mix(in srgb, var(--c) 45%, transparent) 90%);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          opacity: .55;
        }
        .infra-icon {
          width: 44px; height: 44px; border-radius: 12px; display: grid; place-items: center;
          background: color-mix(in srgb, var(--c) 14%, transparent);
          border: 1px solid color-mix(in srgb, var(--c) 42%, transparent);
          color: var(--c);
        }
        .live-badge, .paid-badge {
          position: absolute; top: 14px; right: 14px;
          padding: 3px 8px; border-radius: 999px;
          font-size: 9px; letter-spacing: 0.22em; font-weight: 700;
        }
        .live-badge { color: #00ff9d; background: rgba(0,255,157,.14); border: 1px solid rgba(0,255,157,.45); }
        .paid-badge { color: #ffd34d; background: rgba(255,211,77,.14); border: 1px solid rgba(255,211,77,.5); }
        .featured-crown {
          position: absolute; top: -1px; left: 14px;
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 10px; border-radius: 0 0 8px 8px;
          font-size: 9px; letter-spacing: 0.22em; font-weight: 800;
          color: #05060a;
          background: linear-gradient(180deg, #ffd34d, #ffb020);
          box-shadow: 0 0 22px rgba(255,211,77,.4);
        }
        .cross-sell {
          margin-top: 12px;
          background: linear-gradient(160deg, rgba(255,211,77,.06), rgba(0,0,0,.4));
          border: 1px solid rgba(255,211,77,.28);
          border-radius: 10px;
          padding: 12px;
        }
        .cross-sell-item {
          display: flex; align-items: center; justify-content: space-between; gap: 10px;
          padding: 10px 12px; border-radius: 8px;
          background: color-mix(in srgb, var(--ns-c) 8%, rgba(0,0,0,.3));
          border: 1px solid color-mix(in srgb, var(--ns-c) 30%, transparent);
          transition: transform .25s ease, border-color .2s ease, box-shadow .3s ease;
        }
        .cross-sell-item:hover {
          transform: translateX(2px);
          border-color: var(--ns-c);
          box-shadow: 0 6px 22px -8px color-mix(in srgb, var(--ns-c) 55%, transparent);
        }
        .cross-sell-cta {
          font-family: 'JetBrains Mono', monospace; font-size: 10px;
          letter-spacing: 0.22em; font-weight: 700;
          padding: 6px 10px; border-radius: 999px;
          color: var(--ns-c);
          background: color-mix(in srgb, var(--ns-c) 16%, transparent);
          border: 1px solid color-mix(in srgb, var(--ns-c) 52%, transparent);
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// <InfraTile /> — one card
// ---------------------------------------------------------------------------
const InfraTile = ({ tile, isOpen, onToggle, onRequestQuote }) => {
  const { icon: Icon, kind } = tile;
  return (
    <div className="infra-tile" style={{ '--c': tile.accent }} data-testid={`infra-tile-${tile.id}`}>
      {tile.badge && <span className="featured-crown">👑 {tile.badge}</span>}
      <span className={kind === 'live' ? 'live-badge' : 'paid-badge'}>
        {kind === 'live' ? 'LIVE · FREE' : 'QUOTE'}
      </span>

      <div className="flex items-center gap-3 mb-3 mt-3">
        <div className="infra-icon"><Icon size={20} /></div>
        <div className="min-w-0">
          <div className="text-lg font-bold leading-tight">{tile.name}</div>
          <div className="eh-mono text-[10px] tracking-widest opacity-60">// {tile.tag}</div>
        </div>
      </div>

      <ul className="space-y-1.5 text-xs opacity-85 mt-3 mb-4">
        {tile.bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tile.accent, boxShadow: `0 0 6px ${tile.accent}` }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {kind === 'live' ? (
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-3 py-2 rounded border text-xs eh-mono tracking-widest"
          style={{ borderColor: `${tile.accent}66`, color: tile.accent, background: `${tile.accent}0d` }}
          data-testid={`infra-tile-toggle-${tile.id}`}
        >
          <span className="flex items-center gap-2">{isOpen ? <ChevronUp size={12} /> : <Zap size={12} />} RUN LIVE</span>
          {isOpen ? <ChevronDown size={12} /> : <ArrowRight size={12} />}
        </button>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] eh-mono opacity-80" style={{ color: tile.accent }}>{tile.priceHint}</div>
          <button
            onClick={onRequestQuote}
            className="flex items-center gap-1 px-3 py-2 rounded border text-xs eh-mono tracking-widest"
            style={{ borderColor: `${tile.accent}66`, color: tile.accent, background: `${tile.accent}0d` }}
            data-testid={`infra-tile-quote-${tile.id}`}
          >
            <MessageSquare size={12} /> REQUEST QUOTE
          </button>
        </div>
      )}

      {kind === 'live' && isOpen && <LiveWidget tile={tile} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// <LiveWidget /> — inline input + results + cross-sell CTAs
// ---------------------------------------------------------------------------
const LiveWidget = ({ tile }) => {
  const [val, setVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async (e) => {
    e?.preventDefault?.();
    if (!val.trim()) { toast.error('Enter a value first'); return; }
    setErr(''); setLoading(true); setResult(null);
    try {
      const payload = { [tile.field]: val.trim() };
      const out = await api[tile.apiFn](payload);
      setResult(out);
    } catch (e) {
      if (e?.detail?.limit_reached) {
        setErr(e.detail.message || 'Daily free limit reached — top up wallet');
      } else {
        setErr(e.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 space-y-3" data-testid={`infra-widget-${tile.id}`}>
      <form onSubmit={run} className="flex gap-2">
        <input
          type="text"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={tile.inputPlaceholder}
          className="eh-input flex-1 text-xs"
          data-testid={`infra-input-${tile.id}`}
        />
        <button
          type="submit"
          disabled={loading}
          className="px-3 py-2 rounded border text-xs eh-mono tracking-widest disabled:opacity-50"
          style={{ borderColor: `${tile.accent}66`, color: tile.accent, background: `${tile.accent}0d` }}
          data-testid={`infra-run-${tile.id}`}
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
        </button>
      </form>
      <div className="text-[10px] eh-mono opacity-50">{tile.inputHint}</div>

      {err && (
        <div className="p-3 rounded border border-red-500/40 bg-red-500/10 text-xs eh-mono text-red-300" data-testid={`infra-err-${tile.id}`}>
          {err}
        </div>
      )}

      {result && <ResultBlock tile={tile} data={result} />}
      {result?.next_steps?.length > 0 && <CrossSellBlock items={result.next_steps} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// <ResultBlock /> — renders each result shape differently
// ---------------------------------------------------------------------------
const ResultBlock = ({ tile, data }) => {
  const cp = (val) => { navigator.clipboard.writeText(String(val)); toast.success('Copied'); };

  if (tile.id === 'ip-intel') {
    const r = data.risk || {}, g = data.geo || {};
    const band = r.band || 'safe';
    const bandC = band === 'safe' ? '#00ff9d' : band === 'suspicious' ? '#ffd34d' : band === 'high' ? '#ff8a4d' : '#ff4d6d';
    return (
      <div className="rounded border border-[var(--eh-border)] p-3 space-y-2 text-xs">
        <div className="flex items-center justify-between">
          <div className="eh-mono opacity-70">// {data.ip}{data.resolved_from ? ` ← ${data.resolved_from}` : ''}</div>
          <div className="eh-mono text-[10px] px-2 py-0.5 rounded" style={{ color: bandC, background: `${bandC}18`, border: `1px solid ${bandC}66` }}>
            {String(band).toUpperCase()} · {r.confidence_pct}%
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] opacity-80">
          <div>country: <b>{g.country || '—'}</b></div>
          <div>reports: <b>{r.total_reports}</b></div>
          <div>ISP: <b>{g.isp || '—'}</b></div>
          <div>ASN: <b>{g.asn || '—'}</b></div>
          <div>proxy? <b>{g.proxy ? 'yes' : 'no'}</b></div>
          <div>hosting? <b>{g.hosting ? 'yes' : 'no'}</b></div>
        </div>
      </div>
    );
  }

  if (tile.id === 'whois') {
    const p = data.parsed || {};
    const rows = Object.entries(p).slice(0, 12);
    return (
      <div className="rounded border border-[var(--eh-border)] p-3 space-y-1 text-xs" data-testid="infra-result-whois">
        {rows.length === 0
          ? <pre className="text-[10px] whitespace-pre-wrap opacity-70 max-h-64 overflow-auto">{data.raw?.slice(0, 1500)}</pre>
          : rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-[var(--eh-border)]/60 pb-1">
                <span className="eh-mono opacity-60 text-[10px] uppercase">{k}</span>
                <span className="text-right break-all">{v}</span>
              </div>
            ))
        }
      </div>
    );
  }

  if (tile.id === 'dns') {
    const by = data.by_type || {};
    return (
      <div className="rounded border border-[var(--eh-border)] p-3 space-y-2 text-xs">
        {Object.entries(by).map(([type, rows]) => (
          <div key={type}>
            <div className="eh-mono text-[10px] opacity-60 mb-1">{type}</div>
            {rows.map((r, i) => <div key={i} className="text-[11px] break-all">{r}</div>)}
          </div>
        ))}
      </div>
    );
  }

  if (tile.id === 'subdomains') {
    return (
      <div className="rounded border border-[var(--eh-border)] p-3 space-y-1 text-xs" data-testid="infra-result-subdomains">
        <div className="eh-mono opacity-70">// {data.count} subdomains · {data.source}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 mt-2 max-h-72 overflow-auto pr-1">
          {(data.subdomains || []).map((s, i) => (
            <div key={i} className="flex items-center justify-between gap-2 py-0.5">
              <span className="truncate">{s.host}</span>
              <button onClick={() => cp(s.host)} className="opacity-40 hover:opacity-100"><Copy size={10} /></button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tile.id === 'http-fingerprint') {
    return (
      <div className="rounded border border-[var(--eh-border)] p-3 space-y-2 text-xs">
        <div className="eh-mono opacity-70">// HTTP {data.status} · {data.url}</div>
        {data.tech_stack?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {data.tech_stack.map((t, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full text-[10px] eh-mono" style={{ background: `${tile.accent}18`, color: tile.accent, border: `1px solid ${tile.accent}55` }}>{t}</span>
            ))}
          </div>
        )}
        <details className="text-[10px]">
          <summary className="cursor-pointer opacity-60">show all headers ({Object.keys(data.headers || {}).length})</summary>
          <div className="mt-1 max-h-64 overflow-auto space-y-0.5">
            {Object.entries(data.headers || {}).map(([k, v]) => (
              <div key={k} className="border-b border-[var(--eh-border)]/60 py-1">
                <span className="eh-mono opacity-60">{k}:</span> <span className="break-all">{v}</span>
              </div>
            ))}
          </div>
        </details>
      </div>
    );
  }

  if (tile.id === 'cve-search') {
    return (
      <div className="rounded border border-[var(--eh-border)] p-3 space-y-2 text-xs">
        {(data.results || []).map((row) => {
          const sev = (row.severity || '').toUpperCase();
          const sevC = sev === 'CRITICAL' ? '#ff4d6d' : sev === 'HIGH' ? '#ff8a4d' : sev === 'MEDIUM' ? '#ffd34d' : sev === 'LOW' ? '#4de0ff' : '#8a8a8a';
          return (
            <div key={row.id} className="border-b border-[var(--eh-border)]/60 pb-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <a href={row.url} target="_blank" rel="noreferrer" className="eh-mono font-bold" style={{ color: sevC }}>{row.id} <ExternalLink size={10} className="inline" /></a>
                <span className="text-[10px] eh-mono" style={{ color: sevC }}>{sev} · {row.cvss}</span>
              </div>
              <div className="opacity-80">{row.summary}</div>
            </div>
          );
        })}
        {data.results?.length === 0 && <div className="opacity-60">No results</div>}
      </div>
    );
  }

  if (tile.id === 'username-osint') {
    return (
      <div className="rounded border border-[var(--eh-border)] p-3 space-y-2 text-xs" data-testid="infra-result-username-osint">
        <div className="eh-mono opacity-70">// {data.found_count}/{data.checked} platforms · @{data.username}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 max-h-72 overflow-auto pr-1">
          {(data.found || []).map((r) => (
            <a key={r.platform} href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-white opacity-80">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00ff9d] shrink-0" />
              <span>{r.platform}</span>
              <ExternalLink size={10} className="opacity-60" />
            </a>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// <CrossSellBlock /> — the earning-hook
// ---------------------------------------------------------------------------
const CrossSellBlock = ({ items }) => {
  const navigate = useNavigate();
  return (
    <div className="cross-sell" data-testid="infra-cross-sell">
      <div className="flex items-center gap-2 mb-2">
        <Crown size={12} className="text-[#ffd34d]" />
        <div className="eh-mono text-[10px] tracking-[0.3em] text-[#ffd34d]">// NEXT STEP · WE DO IT FOR YOU</div>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div
            key={i}
            className="cross-sell-item"
            style={{ '--ns-c': it.accent }}
            data-testid={`infra-cross-sell-item-${i}`}
          >
            <div className="min-w-0">
              <div className="text-xs font-bold">{it.title}</div>
              <div className="text-[10px] opacity-70 leading-snug">{it.kicker}</div>
              {it.price_hint && <div className="text-[10px] eh-mono mt-0.5" style={{ color: it.accent }}>{it.price_hint}</div>}
            </div>
            <button
              className="cross-sell-cta shrink-0"
              onClick={() => it.to && navigate(it.to)}
            >
              {it.cta} <ArrowRight size={10} className="inline ml-0.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// <QuoteRequestModal /> — for paid infra tiles
// ---------------------------------------------------------------------------
const QuoteRequestModal = ({ tile, onClose }) => {
  const [contact, setContact] = useState('');
  const [channel, setChannel] = useState('telegram');
  const [budget, setBudget] = useState('under_5k');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (contact.trim().length < 3) { toast.error('Add a contact so we can reach out'); return; }
    setBusy(true);
    try {
      const out = await api.infraQuoteRequest({
        service_id:      tile.id,
        service_name:    tile.name,
        contact:         contact.trim(),
        contact_channel: channel,
        budget_hint:     budget,
        note:            note.trim(),
      });
      setDone(out);
      toast.success('Quote request received — check DMs in ~30 min', { duration: 6000 });
    } catch (e) {
      toast.error(e.message || 'Could not submit — try Telegram directly');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border p-5 sm:p-6 relative" style={{ borderColor: tile.accent, background: 'linear-gradient(160deg, rgba(255,255,255,.03), #05060a 60%)' }} onClick={(e) => e.stopPropagation()} data-testid="infra-quote-modal">
        <button onClick={onClose} className="absolute top-3 right-3 opacity-60 hover:opacity-100" data-testid="infra-quote-close"><X size={16} /></button>

        {done ? (
          <div className="text-center py-4">
            <Sparkles size={28} style={{ color: tile.accent }} className="mx-auto mb-3" />
            <div className="text-xl font-bold nameplate-serif">Request Received</div>
            <div className="text-xs opacity-70 mt-1">{done.message}</div>
            {done.wallet_bonus > 0 && (
              <div className="mt-4 inline-block px-4 py-2 rounded-full border border-[#00ff9d]/40 bg-[#00ff9d]/10 text-xs eh-mono">
                ⚡ +₹{done.wallet_bonus} credited to your wallet
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-2">
              <Link to="/me/wallet" className="eh-btn-ghost text-xs">// MY WALLET</Link>
              <button onClick={onClose} className="eh-btn-ghost text-xs">CLOSE</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="infra-icon" style={{ '--c': tile.accent }}>
                <tile.icon size={18} />
              </div>
              <div>
                <div className="text-lg font-bold nameplate-serif">Get quote · {tile.name}</div>
                <div className="eh-mono text-[10px] opacity-60 tracking-widest">// {tile.priceHint}</div>
              </div>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="eh-mono text-[10px] tracking-widest opacity-70 block mb-1">// CONTACT CHANNEL</label>
                <div className="grid grid-cols-4 gap-1.5">
                  {['telegram', 'email', 'whatsapp', 'phone'].map(c => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setChannel(c)}
                      className={`px-2 py-1.5 rounded text-[10px] eh-mono tracking-widest border ${channel === c ? 'text-black' : 'opacity-70'}`}
                      style={channel === c
                        ? { background: tile.accent, borderColor: tile.accent }
                        : { borderColor: 'var(--eh-border)' }}
                      data-testid={`infra-quote-ch-${c}`}
                    >
                      {c.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="eh-mono text-[10px] tracking-widest opacity-70 block mb-1">// YOUR CONTACT</label>
                <input
                  value={contact} onChange={(e) => setContact(e.target.value)}
                  placeholder={channel === 'email' ? 'you@domain.com' : channel === 'telegram' ? '@handle or t.me/…' : '+91 90000 00000'}
                  className="eh-input w-full text-sm"
                  data-testid="infra-quote-contact"
                />
              </div>
              <div>
                <label className="eh-mono text-[10px] tracking-widest opacity-70 block mb-1">// BUDGET</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { k: 'under_5k',  l: '< ₹5k'   },
                    { k: '5k_20k',    l: '₹5k–20k' },
                    { k: '20k_plus',  l: '> ₹20k'  },
                  ].map(b => (
                    <button type="button" key={b.k}
                      onClick={() => setBudget(b.k)}
                      className={`px-2 py-1.5 rounded text-[10px] eh-mono border ${budget === b.k ? 'text-black' : 'opacity-70'}`}
                      style={budget === b.k
                        ? { background: tile.accent, borderColor: tile.accent }
                        : { borderColor: 'var(--eh-border)' }}
                      data-testid={`infra-quote-budget-${b.k}`}>
                      {b.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="eh-mono text-[10px] tracking-widest opacity-70 block mb-1">// NOTE (optional)</label>
                <textarea
                  value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="target countries, bandwidth, OS, use-case…"
                  rows={2}
                  className="eh-input w-full text-xs"
                  data-testid="infra-quote-note"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 py-3 rounded font-bold text-sm disabled:opacity-50"
                style={{ background: tile.accent, color: '#05060a', boxShadow: `0 8px 26px -8px ${tile.accent}` }}
                data-testid="infra-quote-submit"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {busy ? 'SENDING…' : 'REQUEST QUOTE'}
              </button>
              <div className="text-center text-[10px] eh-mono opacity-60 mt-1">
                <Lock size={9} className="inline mr-1" /> we reply within 30 min · logged-in users earn ₹10 for each request
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default HackingInfraPage;
